// waves (SP2) — real waveform render for the player-view shell. Replaces the
// SP1 stub. Builds (a) an overview band — drawSymRibbon peaks SVG + HiDPI
// drawBarGrid canvas + renderSectionsBySec ribbons + mountPlayheadOverlay — and
// (b) one per-track wave row using drawSymRibbon in the track color.
//
// Fixes the three Phase-0 hypotheses:
//   1+2 aspect distortion → render at the REAL container width (measureWidth +
//       ResizeObserver re-render) AND resample bins to the exact bar count so
//       drawSymRibbon's viewBox matches CSS width 1:1 (no preserveAspectRatio
//       stretch).
//   3   missing per-track peaks → client fallback (decode audio → bins) before
//       rendering rows.
//   4   HiDPI canvas → setupHiDpiCanvas scales the backing store by dpr.

import type { ShareManifest, SummaryPeaks, PerTrackSummaryPeaks } from '@bandway/share-types';
import {
  drawSymRibbon,
  drawBarGrid,
  renderSectionsBySec,
  mountPlayheadOverlay,
  matchSemantic,
} from '@bandway/ui-primitives';
import type { Player } from '../player.js';
import { resampleBins, normalizeBins } from './waves-resample.js';
import { setupHiDpiCanvas } from './waves-hidpi.js';
import { computePerTrackPeaksFallback } from './peaks-fallback.js';
import { mountSectionHandlers } from '../ui/section-handlers.js';
import { mountLoopIndicator } from '../ui/section-loop-binding.js';
import { abbrevSection } from '../chart/section-strip.js';
import { t } from '../ui/i18n.js';
import {
  clampFactor,
  factorFromPinch,
  pointerDistance,
  scrollLeftForAnchor,
  scrollLeftForPlayheadAtStart,
  zoomLabel,
} from './overview-pinch.js';

const H = 80;             // logical band height (px)
const ROW_H = 40;         // per-track row height (px)
const BAR_W = 2;
const BAR_GAP = 1;
const STRIDE = BAR_W + BAR_GAP;
// Подпись секции — плашка 11px bold, caps, разрядка. Ширина текста ≈ CHAR px на букву (замер на живой волне:
// «VERSE 1» ≈ 7.1, «V1» ≈ 7.4, «PC» ≈ 8.4, одиночная «O» ≈ 9.2); CHROME — всё, что плашка занимает кроме
// текста: поля, отступы от краёв секции (max-width: calc(100% - 12px)) и рамка секции. У полного имени —
// обычная плашка, у сокращения и первой буквы — тесная (`.is-compact`, player-view.css): иначе «V1» не
// влезало в 36-пиксельную секцию телефона и обрезалось до «V…». Запас на букву — сознательно в сторону
// «сократить лишний раз», а не «обрезать многоточием».
const LABEL_FULL = { charPx: 8, chromePx: 29 };
const LABEL_SHORT = { charPx: 8.5, chromePx: 17 };
const labelFits = (len: number, px: number, k: { charPx: number; chromePx: number }): boolean =>
  px >= len * k.charPx + k.chromePx;

type Track = ShareManifest['tracks'][number];
type Section = ShareManifest['sections'][number];

export interface WavesDeps {
  manifest: ShareManifest;
  peaks: SummaryPeaks;
  perTrackPeaks: PerTrackSummaryPeaks | null;
  player: Player;
  /** Measure the live render width (px). Default: host.clientWidth. */
  measureWidth?: (el: HTMLElement) => number;
  /** devicePixelRatio for HiDPI canvas. Default: window.devicePixelRatio. */
  devicePixelRatio?: number;
  /** Subscribe to width changes; return an unsubscribe. Default: ResizeObserver. */
  observeResize?: (el: HTMLElement, cb: () => void) => () => void;
  /** Override per-track fallback computation (tests inject a stub). */
  computeFallback?: (
    manifest: ShareManifest,
    existing: PerTrackSummaryPeaks | null,
  ) => Promise<PerTrackSummaryPeaks>;
  /** Render per-track wave lanes under the overview. Default true. Playback
   *  (stage) layout sets false — it shows only the overview band. */
  showLanes?: boolean;
  /** Петля сменилась (id секции или null). Телефонный экран показывает её в транспорте. */
  onLoopChange?: (sectionId: string | null) => void;
}

export interface WavesHandle {
  unmount: () => void;
  /** Set the overview zoom factor (clamped 1×–12×). 1× = whole song fits the
   *  band; >1× widens the content and enables horizontal scroll. Exposed for the
   *  pinch gesture and for tests. `anchorClientX` — x of the fingers: the point
   *  under them stays under them; without it the view is left where it was. */
  setZoom: (factor: number, anchorClientX?: number) => void;
  /** Поставить / снять петлю снаружи (✕ в транспорте телефона). */
  setLoop: (sectionId: string | null) => void;
  getLoop: () => string | null;
}

function defaultObserveResize(el: HTMLElement, cb: () => void): () => void {
  if (typeof ResizeObserver === 'undefined') return () => {};
  const ro = new ResizeObserver(() => cb());
  ro.observe(el);
  return () => ro.disconnect();
}

interface DecodeEnv {
  OfflineAudioContext?: typeof OfflineAudioContext;
  AudioContext?: typeof AudioContext;
}

/**
 * ONE decoding context for a whole peaks fallback, not one per stem
 * (B-260927-share-web-peaks-fallback-contexts): iOS Safari caps concurrent
 * AudioContexts, and every stem past the cap was left without a wave.
 * OfflineAudioContext is preferred — it is not bound to the audio hardware and
 * decodeAudioData works on it any number of times (resampling to its rate is
 * irrelevant for peaks). A realtime AudioContext is the fallback, reused and
 * closed in dispose().
 */
export function createFallbackDecoder(env: DecodeEnv = globalThis as DecodeEnv): {
  decodeAudio: (url: string) => Promise<AudioBuffer>;
  dispose: () => Promise<void>;
} {
  let ctx: BaseAudioContext | null = null;
  let realtime = false;
  const context = (): BaseAudioContext => {
    if (ctx) return ctx;
    if (env.OfflineAudioContext) {
      ctx = new env.OfflineAudioContext(1, 1, 48000);
    } else {
      ctx = new (env.AudioContext as typeof AudioContext)();
      realtime = true;
    }
    return ctx;
  };
  return {
    decodeAudio: async (url: string): Promise<AudioBuffer> => {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      return context().decodeAudioData(arr);
    },
    dispose: async (): Promise<void> => {
      if (ctx && realtime) { try { await (ctx as AudioContext).close?.(); } catch { /* already closed */ } }
      ctx = null;
    },
  };
}

function defaultFallback(binCount: number) {
  return async (
    manifest: ShareManifest,
    existing: PerTrackSummaryPeaks | null,
  ): Promise<PerTrackSummaryPeaks> => {
    const decoder = createFallbackDecoder();
    try {
      return await computePerTrackPeaksFallback(manifest, existing, { decodeAudio: decoder.decodeAudio, binCount });
    } finally {
      await decoder.dispose();
    }
  };
}

export function mountWaves(host: Element, deps: WavesDeps): WavesHandle {
  const {
    manifest, peaks, perTrackPeaks, player,
    measureWidth = (el) => el.clientWidth || 0,
    devicePixelRatio = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
    observeResize = defaultObserveResize,
    computeFallback = defaultFallback(peaks.binCount),
    showLanes = true,
  } = deps;

  const wrap = document.createElement('div');
  wrap.className = 'pv-waves';

  // --- Overview band ---
  // `band` is the scroll VIEWPORT; `content` is the zoomable inner box whose
  // width is `viewportWidth × zoomFactor`. At 1× content == viewport (no
  // scroll); zooming widens content so sections/grid spread out and the band
  // scrolls horizontally. All render layers (svg / grid / sections / playhead)
  // live inside `content` so they fill the zoomed box, not the viewport.
  const band = document.createElement('div');
  band.className = 'pv-overview-band';
  const content = document.createElement('div');
  content.className = 'pv-overview-content';
  band.appendChild(content);
  wrap.appendChild(band);

  const overlay = mountPlayheadOverlay(content, {
    getPositionSec: () => player.getPlayhead(),
    getTotalDurationSec: () => manifest.duration,
    // Без onClickSeek: перемотку кликом по волне делает ОДИН обработчик —
    // mountSectionHandlers (секция → её начало, зазор → точка). Оверлей тоже
    // перематывал, и тап по секции давал две перемотки (разведка 16.10).
  });

  // --- Per-track rows host (hidden in playback/stage layout) ---
  const rowsWrap = document.createElement('div');
  rowsWrap.className = 'pv-wave-rows';
  if (showLanes) wrap.appendChild(rowsWrap);

  // Render the overview band at a given width (idempotent — clears + rebuilds
  // the peaks SVG + grid canvas + sections, keeps the playhead overlay alive).
  let svgEl: SVGElement | null = null;
  let gridCanvas: HTMLCanvasElement | null = null;
  let sectionsLayer: HTMLDivElement | null = null;

  const renderOverview = (width: number): void => {
    const w = Math.max(1, Math.floor(width));
    // The band stretches to the transport height (W2), so render the wave + grid
    // at the REAL band height — not the fixed H — or the bar-grid canvas only
    // covers a top strip ("grid ends at half the wave", W4 Сергей 2026-06-22).
    const bandH = Math.max(1, Math.round(band.clientHeight) || H);
    const barCount = Math.max(1, Math.floor(w / STRIDE));
    const bins = normalizeBins(resampleBins(peaks.peaks, barCount));

    // Layer 1 — peaks ribbon at REAL width + height (viewBox === css box → no stretch).
    const tmp = document.createElement('div');
    tmp.innerHTML = drawSymRibbon(bins, { width: w, height: bandH, fillStyle: 'var(--fg-2)', barWidth: BAR_W, barGap: BAR_GAP });
    const newSvg = tmp.querySelector('svg');
    if (newSvg) {
      newSvg.setAttribute('class', 'pv-overview-svg');
      // Position-based insertion (prepend / .after) instead of
      // insertBefore(refNode): the ref-node form throws NotFoundError if the ref
      // ever isn't a live child of `content` (observed transiently under vite
      // HMR re-mounts). prepend/replaceChild can't throw that way.
      if (svgEl && svgEl.parentNode === content) content.replaceChild(newSvg, svgEl);
      else content.prepend(newSvg);
      svgEl = newSvg;
    }

    // Layer 2 — HiDPI bar-grid canvas.
    const newCanvas = document.createElement('canvas');
    newCanvas.className = 'pv-bar-grid';
    const ctx = setupHiDpiCanvas(newCanvas, w, bandH, devicePixelRatio);
    if (ctx && manifest.duration > 0) {
      drawBarGrid(ctx, {
        bpm: manifest.song.bpm,
        timeSignature: manifest.song.timeSignature,
        durationSec: manifest.duration,
        pxPerSec: w / manifest.duration,
        height: bandH,
        barOffsetMs: manifest.song.barOffsetMs ?? 0,
      });
    }
    if (gridCanvas && gridCanvas.parentNode === content) content.replaceChild(newCanvas, gridCanvas);
    else if (svgEl && svgEl.parentNode === content) svgEl.after(newCanvas); // right above the svg's next sibling
    else content.prepend(newCanvas);
    gridCanvas = newCanvas;

    // Layer 3 — sections (percentage-based; width-independent, render once).
    if (!sectionsLayer) {
      sectionsLayer = document.createElement('div');
      sectionsLayer.className = 'pv-sections';
      const els = renderSectionsBySec(
        manifest.sections.map((s) => ({
          id: s.id, name: s.name, startSec: s.startSec, endSec: s.endSec,
          color: matchSemantic(s.name) || 'var(--fg-3)',
        })),
        manifest.duration, 0,
      );
      els.forEach((el, i) => {
        sectionsLayer!.appendChild(el);
        const nameEl = el.querySelector<HTMLElement>('.rh-section-name');
        if (nameEl) sectionLabels.push({ sec: manifest.sections[i], el, nameEl });
      });
      content.appendChild(sectionsLayer);
    }
    labelSections(w);
  };

  // Подписи секций по ширине в пикселях (а не «влезло или обрезалось многоточием»: на узкой секции
  // оставалось «I…», «V…»). Зовётся после КАЖДОЙ отрисовки обзора — первой, ресайза и зума. Ступени:
  // полное имя → сокращение («Verse 1» → V1, «Pre-Chorus» → PC) → первая буква → пусто. Полное имя
  // остаётся в title и aria-label секции.
  const sectionLabels: Array<{ sec: Section; el: HTMLElement; nameEl: HTMLElement }> = [];
  const labelFor = (name: string, px: number): { text: string; compact: boolean } => {
    if (labelFits(name.length, px, LABEL_FULL)) return { text: name, compact: false };
    const abbr = abbrevSection(name);
    if (abbr && labelFits(abbr.length, px, LABEL_SHORT)) return { text: abbr, compact: true };
    const letter = [...abbr][0] ?? '';
    if (letter && labelFits(1, px, LABEL_SHORT)) return { text: letter, compact: true };
    return { text: '', compact: false };
  };
  const labelSections = (contentWidth: number): void => {
    if (!(manifest.duration > 0)) return;
    for (const { sec, el, nameEl } of sectionLabels) {
      const name = (sec.name ?? '').trim();
      if (!name) { nameEl.hidden = true; continue; }          // безымянная: нечего подписывать
      el.title = name;
      el.setAttribute('aria-label', name);
      const px = ((sec.endSec - sec.startSec) * contentWidth) / manifest.duration;
      const { text, compact } = labelFor(name, px);
      nameEl.textContent = text;
      nameEl.classList.toggle('is-compact', compact);
      nameEl.hidden = text === '';                            // пустая плашка рисовалась бы тёмным квадратиком
    }
  };

  // Render one per-track row from bins (or .is-empty when absent).
  const renderRow = (track: Track, bins: number[] | undefined, width: number): void => {
    const row = document.createElement('div');
    row.className = 'pv-wave-row';
    row.dataset.trackId = track.id;
    if (!bins || bins.length === 0) {
      row.classList.add('is-empty');
    } else {
      const w = Math.max(1, Math.floor(width));
      const barCount = Math.max(1, Math.floor(w / STRIDE));
      const tmp = document.createElement('div');
      tmp.innerHTML = drawSymRibbon(normalizeBins(resampleBins(bins, barCount)), {
        width: w, height: ROW_H, fillStyle: track.color, barWidth: BAR_W, barGap: BAR_GAP,
      });
      const svg = tmp.querySelector('svg');
      if (svg) { svg.setAttribute('class', 'pv-mini-wave'); row.appendChild(svg); }
    }
    rowsWrap.appendChild(row);
  };

  const renderRows = (data: PerTrackSummaryPeaks | null, width: number): void => {
    rowsWrap.textContent = '';
    for (const t of manifest.tracks) renderRow(t, data?.tracks[t.id], width);
  };

  host.appendChild(wrap);
  overlay.start();

  // Section gestures (one handler for both screens): tap a section → ONE seek to
  // its start; double tap / double click → toggle a loop over it; tap in a gap →
  // seek to that point; Esc → clear. The loop indicator paints `.is-loop-active`
  // on the looped section (outline via player-view.css). Delegated on `band`,
  // so it survives section re-renders.
  const loopIndicator = mountLoopIndicator(band);
  let activeLoopId: string | null = null;

  // Метка петли на волне: имя секции + ✕. Живёт в `content`, поэтому едет вместе
  // с зумом; data-wave-control — чтобы тап по метке не считался тапом по волне.
  const loopTag = document.createElement('div');
  loopTag.className = 'pv-loop-tag';
  loopTag.dataset.waveControl = 'true';
  loopTag.hidden = true;
  const loopName = document.createElement('span');
  loopName.className = 'pv-loop-tag-name';
  const loopOff = document.createElement('button');
  loopOff.type = 'button';
  loopOff.className = 'pv-loop-tag-off';
  loopOff.setAttribute('aria-label', t('wave.loopOff'));
  loopOff.title = t('wave.loopOff');
  loopOff.textContent = '✕';
  loopTag.appendChild(loopName);
  loopTag.appendChild(loopOff);
  content.appendChild(loopTag);

  const applyLoop = (sectionId: string | null): void => {
    const sec = sectionId ? manifest.sections.find((s) => s.id === sectionId) : undefined;
    const nextId = sec ? sec.id : null;
    // Ничего не поменялось (двойной тап по B при петле на B после переноса, Esc без
    // петли) — не дёргаем ни плеер, ни onLoopChange второй раз.
    if (nextId === activeLoopId) return;
    activeLoopId = nextId;
    player.setActiveLoopSection(sec ? { startSec: sec.startSec, endSec: sec.endSec } : null);
    loopIndicator.setActive(activeLoopId);
    loopTag.hidden = !sec;
    if (sec && manifest.duration > 0) {
      loopName.textContent = `⟲ ${sec.name || t('songScreen.loop')}`;   // безымянная секция — не пустая метка
      // Секция в правой половине: якорь по её КОНЦУ (right), иначе nowrap-метка
      // вылезает за волну и band получает горизонтальный скролл уже на 1×.
      const rightHalf = sec.startSec / manifest.duration > 0.5;
      loopTag.classList.toggle('is-right', rightHalf);
      if (rightHalf) {
        loopTag.style.left = '';
        loopTag.style.right = `${((manifest.duration - sec.endSec) / manifest.duration) * 100}%`;
      } else {
        loopTag.style.right = '';
        loopTag.style.left = `${(sec.startSec / manifest.duration) * 100}%`;
      }
    }
    deps.onLoopChange?.(activeLoopId);
  };
  // Без stopPropagation: от клика защищает data-wave-control, а глушить его для
  // документных обработчиков (click-outside у меню и шторок) незачем.
  loopOff.addEventListener('click', () => applyLoop(null));
  const sectionHandlers = mountSectionHandlers(band, manifest.sections, {
    getLoop: () => activeLoopId,
    // Петля едет за тапом: тап по другой секции, пока петля включена, переносит
    // её туда (как в прототипе v3) — иначе перемотка вне петли играла бы мимо неё.
    onSeek: (sec, id) => {
      if (activeLoopId && id !== activeLoopId) applyLoop(id);
      player.seek(sec);
    },
    onToggleLoop: (sectionId, before) => {
      if (sectionId === '') { applyLoop(null); return; }
      applyLoop(before === sectionId ? null : sectionId);
    },
    onBackgroundClick: (ev) => {
      const r = content.getBoundingClientRect();
      if (!(r.width > 0) || !(manifest.duration > 0)) return;
      const sec = ((ev.clientX - r.left) / r.width) * manifest.duration;
      player.seek(Math.min(manifest.duration, Math.max(0, sec)));
    },
  });

  // Свежая волна петли не имеет, а движок мог унаследовать её от прошлого экрана
  // (широкий ↔ телефонный экран пересобираются на переходе через порог, плеер при
  // этом живёт): невидимая петля, оставленная в движке, крутила бы звук без метки
  // и без ✕. Сброс здесь делает «волна и движок говорят одно»; телефонный экран
  // возвращает свою петлю уже ПОСЛЕ монтажа (phone-screen.ts, setLoop из памяти).
  player.setActiveLoopSection(null);

  // --- Zoom state ---------------------------------------------------------
  // `factor` widens the content box; `baseWidth` is the last measured viewport
  // width. content width = baseWidth × factor. At 1× they're equal (no scroll).
  let factor = 1;
  let baseWidth = measureWidth(wrap) || 1000;
  let curContentWidth = baseWidth;
  let followRaf = 0;
  // Ведение: вид едет за плейхедом ТОЛЬКО при игре и пока человек сам не сдвинул
  // вид (щипок или прокрутка). Снова включается по НАСТОЯЩЕМУ старту игры (кадр, на
  // котором isPlaying стал true после false — см. followTick) и по сбросу зума.
  // По событию `playing` нельзя: player.seek() при игре делает pause()+play()
  // синхронно, а обёртка петли зовёт seek() на каждом круге — ручной сдвиг вида
  // отменялся бы на ближайшем круге.
  let userPanned = false;
  let lastSetScroll = -1;
  let wasPlaying = false;
  // Моки плеера в тестах могут не иметь isPlaying — гард оставлен.
  const isPlaying = (): boolean => (typeof player.isPlaying === 'function' ? player.isPlaying() : false);

  const cancelRaf = (id: number): void => {
    if (id && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
  };
  const raf = (cb: FrameRequestCallback): number =>
    (typeof requestAnimationFrame === 'function')
      ? requestAnimationFrame(cb)
      : (setTimeout(() => cb(0), 16) as unknown as number);

  // Все наши записи scrollLeft идут через setScroll: запомненное значение
  // отличает прокрутку, которую сделали МЫ, от ручной (см. onScroll).
  const setScroll = (x: number): void => { band.scrollLeft = x; lastSetScroll = band.scrollLeft; };

  // Pin the current playhead to the LEFT edge of the visible window when zoomed
  // (Сергей's choice). At 1× there is nothing to scroll.
  const pinPlayheadToStart = (): void => {
    if (factor <= 1) { setScroll(0); return; }
    setScroll(scrollLeftForPlayheadAtStart(
      player.getPlayhead(), manifest.duration, curContentWidth,
    ));
  };

  // «2×» — метка зума поверх окна волны (живёт в wrap, не в content: не едет с
  // прокруткой). Видна только при зуме; тап возвращает 1×.
  const zoomTag = document.createElement('button');
  zoomTag.type = 'button';
  zoomTag.className = 'pv-zoom-tag';
  zoomTag.dataset.waveControl = 'true';
  zoomTag.hidden = true;
  zoomTag.setAttribute('aria-label', t('wave.zoomReset'));
  zoomTag.title = t('wave.zoomReset');
  wrap.appendChild(zoomTag);

  const applyRender = (anchor?: { frac: number; offset: number }): void => {
    curContentWidth = Math.max(1, Math.round(baseWidth * factor));
    content.style.width = `${curContentWidth}px`;
    renderOverview(curContentWidth);
    zoomTag.hidden = factor <= 1.01;
    zoomTag.textContent = zoomLabel(factor);
    if (factor <= 1) { setScroll(0); return; }
    if (anchor) setScroll(scrollLeftForAnchor(anchor.frac, anchor.offset, curContentWidth, baseWidth));
    else if (isPlaying() && !userPanned) pinPlayheadToStart();
  };

  // Follow loop: while zoomed AND playing AND the reader hasn't taken the view
  // into their own hands, keep the playhead pinned to the left edge as playback
  // advances. A real play start (isPlaying false → true between two frames) gives
  // the view back to the playhead; a synchronous pause()+play() from seek() never
  // shows up as a transition. Self-stops at 1×.
  const followTick = (): void => {
    if (factor <= 1) { followRaf = 0; return; }
    const playing = isPlaying();
    if (playing && !wasPlaying) userPanned = false;
    wasPlaying = playing;
    if (playing && !userPanned) pinPlayheadToStart();
    followRaf = raf(followTick);
  };
  // wasPlaying синхронизируем при запуске цикла: пока зума не было, он не
  // обновлялся, и устаревшее «не играло» сбросило бы щипок первым же кадром.
  const startFollow = (): void => {
    wasPlaying = isPlaying();
    followRaf = raf(followTick);
  };

  // anchorClientX — где стоят пальцы (середина щипка): зум идёт вокруг них, а не
  // вокруг плейхеда. Без него (тест, метка «2×») — вид остаётся как был.
  const setZoom = (next: number, anchorClientX?: number): void => {
    let anchor: { frac: number; offset: number } | undefined;
    if (anchorClientX !== undefined) {
      const r = band.getBoundingClientRect();
      const offset = anchorClientX - r.left;
      anchor = { frac: (band.scrollLeft + offset) / Math.max(1, curContentWidth), offset };
    }
    factor = clampFactor(next);
    if (factor <= 1) userPanned = false;
    applyRender(anchor);
    if (factor > 1 && followRaf === 0) startFollow();
  };

  // Без stopPropagation — см. loopOff: защита от тапа по волне это data-wave-control.
  zoomTag.addEventListener('click', () => setZoom(1));

  // Ручная прокрутка зумленой волны выключает ведение (наша собственная запись
  // scrollLeft отличается от «человеческой» тем, что совпадает с lastSetScroll).
  const onScroll = (): void => {
    if (factor > 1 && Math.abs(band.scrollLeft - lastSetScroll) > 2) userPanned = true;
  };
  band.addEventListener('scroll', onScroll);

  // Initial render at the measured width (1×).
  applyRender();

  // Per-track: render present bins immediately, then fill missing via fallback.
  if (showLanes) {
    renderRows(perTrackPeaks, baseWidth);
    void computeFallback(manifest, perTrackPeaks).then((filled) => {
      renderRows(filled, baseWidth);
    }).catch(() => { /* keep .is-empty rows */ });
  }

  // Re-render on viewport width change (keeps the current zoom factor).
  const stopObserve = observeResize(wrap, () => {
    const w = measureWidth(wrap);
    if (w > 0 && w !== baseWidth) {
      baseWidth = w;
      applyRender();
      if (showLanes) renderRows(perTrackPeaks, baseWidth);
    }
  });

  // --- Pinch-to-zoom (touch only) -----------------------------------------
  // Two touch pointers on the band drive a continuous zoom factor around the
  // midpoint of the fingers; the re-render is rAF-throttled. Mouse pointers are
  // ignored → desktop-web has no zoom (by design). The band's CSS is
  // `touch-action: pan-x pan-y` (one finger scrolls the page vertically and the
  // zoomed content horizontally); while a 2-finger gesture is live we own it
  // (touch-action:none) so mobile Safari doesn't page-zoom. A pinch also takes
  // the view out of "follow the playhead" — the reader looks where they pinch.
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchStartDist = 0;
  let pinchStartFactor = 1;
  let zoomRaf = 0;
  let pendingFactor = factor;
  let pendingAnchorX: number | undefined;
  const flushZoom = (): void => { zoomRaf = 0; setZoom(pendingFactor, pendingAnchorX); };
  const scheduleZoom = (f: number, centerX: number): void => {
    pendingFactor = f;
    pendingAnchorX = centerX;
    if (zoomRaf) return;
    zoomRaf = raf(flushZoom);
  };
  const onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchStartDist = pointerDistance(a, b);
      pinchStartFactor = factor;
      userPanned = true;
      band.style.touchAction = 'none';
    }
  };
  const onPointerMove = (e: PointerEvent): void => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2 && pinchStartDist > 0) {
      const [a, b] = [...pointers.values()];
      scheduleZoom(factorFromPinch(pinchStartDist, pointerDistance(a, b), pinchStartFactor), (a.x + b.x) / 2);
    }
  };
  const onPointerUp = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) { pinchStartDist = 0; band.style.touchAction = ''; }
  };
  // Страховка: браузеры фиксируют touch-action в начале касания, поэтому
  // переключение на `none` на втором pointerdown может не сработать — тогда
  // два пальца на волне зумили бы всю страницу. Неотменяемый слушатель гасит это.
  const onTouchMove = (e: TouchEvent): void => { if (e.touches.length > 1) e.preventDefault(); };
  band.addEventListener('touchmove', onTouchMove, { passive: false });
  band.addEventListener('pointerdown', onPointerDown);
  band.addEventListener('pointermove', onPointerMove);
  band.addEventListener('pointerup', onPointerUp);
  band.addEventListener('pointercancel', onPointerUp);

  return {
    setZoom,
    setLoop: (id) => applyLoop(id),
    getLoop: () => activeLoopId,
    unmount: () => {
      stopObserve();
      cancelRaf(followRaf); cancelRaf(zoomRaf);
      band.removeEventListener('scroll', onScroll);
      zoomTag.remove();
      band.removeEventListener('touchmove', onTouchMove);
      band.removeEventListener('pointerdown', onPointerDown);
      band.removeEventListener('pointermove', onPointerMove);
      band.removeEventListener('pointerup', onPointerUp);
      band.removeEventListener('pointercancel', onPointerUp);
      sectionHandlers.unmount();
      loopIndicator.unmount();
      overlay.dispose();
      try { host.removeChild(wrap); } catch { /* noop */ }
    },
  };
}
